import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Phone, EnvelopeSimple, MapPin, Tag, Clock, Users, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const statuses = ["new", "contacted", "quoted", "won", "lost"];
const statusColors = {
  new: "bg-blue-100 text-blue-700",
  contacted: "bg-slate-100 text-slate-700",
  quoted: "bg-purple-100 text-purple-700",
  won: "bg-emerald-100 text-emerald-700",
  lost: "bg-slate-100 text-slate-600",
};

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const load = () => api.get("/leads").then(r => setLeads(r.data)).catch(()=>{});
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    await api.patch(`/leads/${id}`, { status });
    load();
  };

  const removeLead = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Lead deleted");
      load();
    } catch {
      toast.error("Failed to delete lead");
    }
  };

  const byStatus = (s) => leads.filter(l => (l.status || "new") === s).length;

  return (
    <div data-testid="leads-page">
      <PageHeader eyebrow="Customer pipeline" title="Leads" description="All quote requests and contact form submissions from your website." />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {statuses.map(s => (
          <Card key={s} className="bg-white border border-slate-200 p-4 rounded-sm">
            <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">{s}</div>
            <div className="font-display text-2xl font-black text-navy mt-1">{byStatus(s)}</div>
          </Card>
        ))}
      </div>
      <Card className="bg-white border border-slate-200 rounded-sm p-0 overflow-hidden">
        {leads.length === 0 ? (
          <div className="text-center text-slate-500 py-16"><Users size={32} className="mx-auto text-slate-300"/><p className="mt-3">No leads yet — the moment someone submits a quote form, they'll show up here.</p></div>
        ) : (
          <div className="divide-y divide-slate-100">
            {leads.map(l => (
              <div key={l.id} data-testid={`lead-${l.id}`} className="p-5 hover:bg-slate-50">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-display font-bold text-navy text-lg">{l.name}</div>
                    <div className="mt-1 flex flex-wrap gap-3 text-xs text-slate-600">
                      <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 hover:text-navy"><EnvelopeSimple size={12}/>{l.email}</a>
                      {l.phone && <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1 hover:text-navy"><Phone size={12}/>{l.phone}</a>}
                      <span className="inline-flex items-center gap-1"><Tag size={12}/>{l.service}</span>
                      <span className="inline-flex items-center gap-1"><Clock size={12}/>{new Date(l.created_at).toLocaleString()}</span>
                    </div>
                    {l.message && <p className="mt-2 text-sm text-slate-700">{l.message}</p>}
                    {l.source && <Badge variant="outline" className="rounded-sm mt-2 text-[10px] uppercase tracking-wider">{l.source}</Badge>}
                  </div>
                  <Select value={l.status || "new"} onValueChange={(v)=>updateStatus(l.id, v)}>
                    <SelectTrigger className={`rounded-sm h-9 w-32 ${statusColors[l.status || "new"]}`}><SelectValue/></SelectTrigger>
                    <SelectContent>{statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  <button data-testid={`lead-del-${l.id}`} onClick={()=>removeLead(l.id)} title="Delete lead" className="p-2 text-slate-400 hover:text-red-500 transition-colors"><Trash size={16}/></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
